<template>
    <div class="assignment-part">
        <div
            class="assignment-question-header"
            @click="collapse()"
            :class="collapsed && 'active'">
            <div class="assignment-question-header-title">
                <div
                    class="drag-handle"
                    v-if="canEditPortfolio">
                    <i class="fas fa-fw fa-arrows-alt-v sub-assignment-part-explanation-drag-handle"></i>
                </div>
                <div class="title">{{ $t('assignment.assignment') }} {{ partIn.order }}</div>
                <b-badge
                    class="badge-part-type d-block d-sm-none"
                    tag="div"
                    variant="info">
                    {{ partIn.getAssignmentPartType() }}
                </b-badge>
            </div>

            <div class="assignment-question-header-tools">
                <b-badge
                    class="badge-part-type d-none d-sm-block"
                    variant="info"
                    >{{ partIn.getAssignmentPartType() }}
                </b-badge>

                <i class="fas fa-fw fa-chevron-down state-icon"></i>

                <b-dropdown
                    class="ellipsis-nav assignment-part-nav"
                    v-if="canEditPortfolio"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="assignment-part-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>

                    <b-dropdown-item
                        class="btn-edit edit-assignment-part"
                        @click.prevent.stop="editPart">
                        {{ $t('assignment.edit_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="copy-assignment-part"
                        @click.prevent.stop="copyPart">
                        <i class="btn-duplicate"></i>{{ $t('assignment.copy_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="remove-assignment-part"
                        @click.prevent.stop="onRemovePart">
                        <i class="btn-remove"></i>{{ $t('assignment.delete_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>

            <design-2021-assignment-edit-update-assignment-part-modal-component
                :profile-categories-in="profileCategoriesIn"
                :part-in="partIn">
            </design-2021-assignment-edit-update-assignment-part-modal-component>
        </div>

        <b-collapse
            class="part-content"
            v-model="collapsed"
            :id="'collapse-part-' + partIn.id"
            role="tabpanel">
            <div class="assignment-content">
                <div
                    class="assignment-title"
                    :class="!partIn.name ? 'no-title' : null">
                    {{ partIn.name ? partIn.name : $t('assignment.no_title_given') }}
                </div>

                <div class="assignment-text">
                    <design-2021-apprentice-display-html-component
                        v-if="partIn.text && partIn.text.value"
                        :html-content="partIn.text.value"></design-2021-apprentice-display-html-component>
                    <div
                        class="min-word-count"
                        v-if="partIn.min_word_count > 0">
                        {{ $t('assignment.minimum_word_count') }}: {{ partIn.min_word_count }}.
                    </div>
                </div>

                <div class="assignment-answer-info">
                    <div
                        class="assignment-type-editor"
                        v-if="partIn.isAssignmentEditor()">
                        <div class="editor-files-update">
                            <b-button
                                class="btn-prep-text"
                                v-if="canEditPortfolio"
                                variant="primary"
                                :href="route">
                                {{ $t('assignment.prepare_text') }}
                            </b-button>
                            <b-button
                                class="btn-upload"
                                v-if="canEditPortfolio"
                                v-b-modal="'assignment-part-editor-blueprint-upload-modal-' + partIn.id"
                                variant="primary">
                                {{ $t('assignment.upload_file') }}
                            </b-button>
                        </div>

                        <design-2021-assignment-part-editor-upload-blueprint-modal-component
                            :id="partIn.id"
                            :group-id="groupId">
                        </design-2021-assignment-part-editor-upload-blueprint-modal-component>
                    </div>


                    <design-2021-assignment-edit-part-fill-in-the-blanks-component
                        v-if="partIn.type_name === 'assignment_fill_in_the_blanks'"
                        @part-updated="updatePart"
                        :can-edit-portfolio="canEditPortfolio"
                        :part-in="partIn">
                    </design-2021-assignment-edit-part-fill-in-the-blanks-component>

                    <design-2021-assignment-edit-part-matching-list-component
                        v-if="partIn.type_name === 'assignment_matching_list'"
                        @part-updated="updatePart"
                        @call-remove-part="onRemovePart"
                        @call-delete-item="deleteItem"
                        :can-edit-portfolio="canEditPortfolio"
                        :ords="true"
                        :part-in="partIn">
                    </design-2021-assignment-edit-part-matching-list-component>

                    <div
                        class="assignment-type-list"
                        v-if="partIn.isAnAssignmentList()">
                        <design-2021-assignment-edit-type-assignment-part-standardlist-component
                            v-if="partIn.isAssignmentStandardList()"
                            :can-edit-portfolio="canEditPortfolio"
                            :part-in="partIn"
                            @call-remove-part="onRemovePart"
                            @call-delete-item="deleteItem">
                        </design-2021-assignment-edit-type-assignment-part-standardlist-component>

                        <design-2021-assignment-edit-type-assignment-part-colorlist-component
                            v-if="partIn.isAssignmentColorList()"
                            :part-in="partIn"
                            :can-edit-portfolio="canEditPortfolio"
                            @call-remove-part="onRemovePart"
                            @call-delete-item="deleteItem">
                        </design-2021-assignment-edit-type-assignment-part-colorlist-component>

                        <design-2021-assignment-edit-type-assignment-part-checklist-component
                            v-if="partIn.isAssignmentCheckList()"
                            :part-in="partIn"
                            :can-edit-portfolio="canEditPortfolio"
                            @call-remove-part="onRemovePart"
                            @call-delete-item="deleteItem">
                        </design-2021-assignment-edit-type-assignment-part-checklist-component>
                    </div>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import { ords } from '../../../../../app';

export default {
    name: 'design-2021-assignment-edit-assignment-part-component',
    props: ['partIn', 'groupId', 'pageIn', 'profileCategoriesIn', 'canEditPortfolio'],
    data() {
        return {
            collapsed: true,
        };
    },
    computed: {
        route() {
            return this.routes.assignmentPartEditorBlueprintEdit(this.partIn.id, this.groupId);
        },
    },
    methods: {
        collapse() {
            this.collapsed = !this.collapsed;
        },

        editPart() {
            this.$bvModal.show(`update-assignment-part-model-${this.partIn.id}`);
        },

        copyPart() {
            this.axios.post(this.routes.assignmentPartCopy(this.partIn.id)).then(
                (response) => {
                    this.pageIn.addRelation('parts', response.data.addedAssignment);

                    if (response.data.updatedAssignment.length > 0) {
                        for (const assignment of response.data.updatedAssignment) {
                            let updateAssignment = {};

                            updateAssignment = this.pageIn.parts.find((item) => item.id === assignment.id);
                            updateAssignment.update({ order: assignment.order });
                        }
                    }
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        updatePart(newPart) {
            ords.hold();

            this.partIn.update(newPart);
            this.alert.success();
            ords.continue();
        },

        onRemovePart() {
            this.alert
                .confirm(this.$t('assignment.delete_part_title'), this.$t('assignment.delete_part_confirm'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving(this.$t('assignment.delete_part_title'), this.$t('assignment.deleting_part'));

                        this.axios.delete(this.routes.assignmentPartDelete(this.partIn.id)).then(
                            (response) => {
                                this.partIn.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        deleteItem(item) {
            this.alert.saving(this.$t('assignment.delete_question'), this.$t('assignment.deleting_question'));
            this.axios.delete(this.routes.assignmentPartListItemDelete(this.partIn.id, item.id)).then(
                (response) => {
                    item.remove();
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },
};
</script>

<style scoped lang="scss">
.assignment-part {
    width: 100%;

    .assignment-question-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: wrap;
        border-bottom: 3px dotted $border-color;
        padding-top: map-get($grid-gutter-spacing, 'ten');
        padding-bottom: map-get($grid-gutter-spacing, 'ten');

        .assignment-question-header-title {
            display: flex;
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
            width: calc(100% - 75px);

            .drag-handle {
                color: var(--primary);
                margin-right: map-get($grid-gutter-spacing, 'eight');

                &:hover {
                    cursor: pointer;
                }
            }

            .title {
                font-weight: $font-weight-bold;
                width: calc(100% - 28px);
            }

            .badge-part-type {
                margin-left: 26px;
                line-height: 1.5;
            }
        }

        .badge-part-type,
        .state-icon {
            align-self: center;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        .badge-part-type {
            margin-left: auto;
            align-self: center;
        }

        .state-icon {
            font-size: $base-font-size;
            transition: transform 400ms ease;
        }

        &.active {
            .state-icon {
                transform: rotate(-180deg);
            }
        }

        &:hover {
            cursor: pointer;
            background-color: $info;
            border-radius: $border-radius-content;

            .title {
                color: $white;
            }
        }
    }

    .assignment-sub-question {
        font-size: $default-font-size;
        color: $text-color;
    }
}

.part-content {
    .assignment-answer-info {
        width: 100%;

        .notice {
            padding: map-get($grid-gutter-spacing, 'fifteen');
            background-color: lighten($text-muted-color, 25%);
            border-radius: $border-radius-content;
        }

        .editor-files-update {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-start;
            gap: map-get($grid-gutter-spacing, 'fifteen') $grid-gutter-width;
            margin-top: map-get($grid-gutter-spacing, 'twenty-one');

            .btn {
                width: 100%;
            }

            .btn-prep-text {
                line-height: 1;
            }
        }
    }
}

.assignment-part-nav ::v-deep {
    .dropdown-toggle {
        line-height: 1;
    }

    .dropdown-menu {
        border: 1px solid $border-color;
    }

    .edit-assignment-part {
        &:before {
            position: relative;
            left: 3px;
            width: 20px;
            margin-right: 12px;
        }
    }

    .copy-assignment-part {
        i {
            color: var(--primary);
        }
    }

    .remove-assignment-part {
        i {
            color: $danger;
        }
    }

    .remove-assignment-part,
    .copy-assignment-part {
        font-size: $default-font-size;

        .dropdown-item {
            display: flex;
            align-items: center;

            i {
                margin-right: map-get($grid-gutter-spacing, 'eight');
                font-size: $base-font-size;
            }
        }
    }
}

.assignment-content {
    display: flex;
    flex-direction: column;
    flex: 1 0;
    flex-wrap: wrap;
    padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen') 0 26px;

    .assignment-title {
        font-weight: $font-weight-medium;
        margin-bottom: map-get($grid-gutter-spacing, 'eight');

        &.no-title {
            font-style: italic;
            color: $text-muted-color;
        }
    }
}

.assignment-text {
    > ::v-deep .display-html-content {
        > *:last-child {
            margin-bottom: 0;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .assignment-part {
        .assignment-question-header {
            flex-wrap: nowrap;

            .assignment-question-header-title {
                width: calc(100% - 200px);
            }

            .assignment-question-header-tools {
                display: flex;
                flex-direction: row;
                align-items: center;
            }
        }
    }

    .part-content {
        .assignment-answer-info {
            .editor-files-update {
                .btn {
                    width: auto;
                }
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .assignment-part {
        .assignment-question {
            .assignment-question-header {
                flex-wrap: nowrap;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .assignment-content {
        flex-wrap: nowrap;
    }
}
</style>
